/**
 * SSPermissions — Effective Permissions Viewer & Permission Checker
 * ✅ Refactored to use shared SS component library (components/ss).
 * API: ssRoleAPI (effectivePerms, checkPerm), ssUserAPI (list) from ssApi.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssRoleAPI, ssUserAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Section,
  Button, Badge, Field, Input, Select,
  Spinner, EmptyState, ErrorBanner,
  DataTable, InfoGrid, StatBar, SearchBar,
  PermissionResultBadge,
  COLOR, FONT,
} from '../../components/ss';

// ── Effective Permissions panel ───────────────────────────────────────────────
function EffectivePermsPanel({ users }) {
  const [userId, setUserId]           = useState('');
  const [search, setSearch]           = useState('');
  const [perms, setPerms]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [err, setErr]                 = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true); setErr('');
    ssRoleAPI.effectivePerms(userId)
      .then(d => setPerms(d.data || d || []))
      .catch(ex => setErr(ex.response?.data?.detail || ex.message || 'Failed to load permissions'))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { if (userId) load(); else setPerms([]); }, [userId, load]);

  const modules  = [...new Set(perms.map(p => p.module).filter(Boolean))].sort();
  const granted  = perms.filter(p => p.isgranted === true).length;
  const denied   = perms.filter(p => p.isgranted === false).length;

  const visible = perms.filter(p => {
    if (moduleFilter && p.module !== moduleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.module?.toLowerCase().includes(q) ||
        p.object?.toLowerCase().includes(q) ||
        p.method?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const COLS = [
    { key: 'module',    label: 'Module',  render: v => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span> },
    { key: 'object',    label: 'Object',  render: v => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span> },
    { key: 'method',    label: 'Method',  render: v => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span> },
    { key: 'entity_id', label: 'Entity',  render: v => <span style={{ color: COLOR.grayLight }}>{v || '*'}</span> },
    { key: 'isgranted', label: 'Result',  render: v => <PermissionResultBadge isGranted={v} /> },
  ];

  return (
    <Card style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 15, fontWeight: FONT.weight.bold, color: COLOR.text, marginBottom: 10 }}>
        🔍 Effective Permissions Viewer
      </div>
      <p style={{ fontSize: FONT.size.base, color: COLOR.textMuted, margin: '0 0 16px' }}>
        Reads <code style={{ background: COLOR.graySubtle, padding: '1px 5px', borderRadius: 4 }}>ssvw_userspermissions</code> — the resolved allow/deny result after role + user override precedence.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Select User" style={{ flex: '1 1 260px', marginBottom: 0 }}>
          <Select value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">— pick a user —</option>
            {users.map(u => <option key={u.userid} value={u.userid}>{u.username} ({u.email})</option>)}
          </Select>
        </Field>
        {userId && (
          <>
            <Field label="Filter by Module" style={{ flex: '1 1 180px', marginBottom: 0 }}>
              <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
                <option value="">All modules</option>
                {modules.map(m => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Field>
            <Field label="Search object / method" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <Input placeholder="e.g. party, read" value={search} onChange={e => setSearch(e.target.value)} />
            </Field>
          </>
        )}
      </div>

      <ErrorBanner>{err}</ErrorBanner>

      {userId && !loading && perms.length > 0 && (
        <StatBar style={{ marginBottom: 14 }} stats={[
          { label: 'Granted', value: granted,      color: COLOR.greenDark },
          { label: 'Denied',  value: denied,       color: COLOR.redDark },
          { label: 'Total',   value: perms.length, color: COLOR.gray },
        ]} />
      )}

      {loading ? <Spinner /> : userId && (
        perms.length === 0
          ? <EmptyState icon="🔑" title="No permissions found" message="This user has no permission rules." />
          : (
            <>
              <DataTable columns={COLS} rows={visible} rowKey={(_, i) => i} minWidth={580} />
              {visible.length < perms.length && (
                <div style={{ fontSize: 12, color: COLOR.grayLight, marginTop: 8, textAlign: 'right' }}>
                  Showing {visible.length} of {perms.length} rules
                </div>
              )}
            </>
          )
      )}
    </Card>
  );
}

// ── Ad-hoc Permission Checker ─────────────────────────────────────────────────
function PermissionChecker({ users }) {
  const [form, setForm] = useState({ user_id: '', module: '', object: '', method: '', entity_id: '' });
  const [result, setResult]     = useState(null);
  const [checking, setChecking] = useState(false);
  const [err, setErr]           = useState('');

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function check(e) {
    e.preventDefault();
    if (!form.user_id || !form.module || !form.object || !form.method) {
      setErr('User, module, object and method are all required.'); return;
    }
    setChecking(true); setErr(''); setResult(null);
    try {
      const res = await ssRoleAPI.checkPerm({
        user_id:   Number(form.user_id),
        module:    form.module,
        object:    form.object,
        method:    form.method,
        entity_id: form.entity_id || null,
      });
      setResult(res.data || res);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || 'Check failed');
    } finally { setChecking(false); }
  }

  const granted = result?.isgranted ?? result?.granted ?? result?.allowed ?? result?.result;

  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: FONT.weight.bold, color: COLOR.text, marginBottom: 10 }}>
        ⚡ Ad-hoc Permission Check
      </div>
      <p style={{ fontSize: FONT.size.base, color: COLOR.textMuted, margin: '0 0 16px' }}>
        Calls <code style={{ background: COLOR.graySubtle, padding: '1px 5px', borderRadius: 4 }}>POST /permissions/check</code> — the same fast-path used by all API endpoints.
      </p>

      <ErrorBanner>{err}</ErrorBanner>

      <form onSubmit={check}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, marginBottom: 18 }}>
          <Field label="User" required>
            <Select value={form.user_id} onChange={upd('user_id')}>
              <option value="">— select user —</option>
              {users.map(u => <option key={u.userid} value={u.userid}>{u.username}</option>)}
            </Select>
          </Field>
          <Field label="Module" required>
            <Input placeholder="e.g. parties" value={form.module} onChange={upd('module')} />
          </Field>
          <Field label="Object" required>
            <Input placeholder="e.g. party" value={form.object} onChange={upd('object')} />
          </Field>
          <Field label="Method" required>
            <Input placeholder="e.g. read" value={form.method} onChange={upd('method')} />
          </Field>
          <Field label="Entity ID (optional)">
            <Input placeholder="row-level target" value={form.entity_id} onChange={upd('entity_id')} />
          </Field>
        </div>
        <Button type="submit" disabled={checking}>
          {checking ? 'Checking…' : '⚡ Check Permission'}
        </Button>
      </form>

      {result !== null && (
        <div style={{
          marginTop: 20, padding: '18px 22px', borderRadius: 10,
          border: `2px solid ${granted ? COLOR.greenBorder : COLOR.redBorder}`,
          background: granted ? COLOR.greenBg : COLOR.redBg,
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <span style={{ fontSize: 36 }}>{granted ? '✅' : '🚫'}</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: FONT.weight.bold, color: granted ? COLOR.greenDark : COLOR.redDark }}>
              {granted ? 'Access Granted' : 'Access Denied'}
            </div>
            <div style={{ fontSize: FONT.size.base, color: granted ? '#166534' : '#991b1b', marginTop: 4 }}>
              {form.module}.{form.object}.{form.method}
              {form.entity_id ? ` [entity: ${form.entity_id}]` : ''}
              {' '}for user #{form.user_id}
            </div>
            {result.reason && (
              <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 6 }}>Reason: {result.reason}</div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSPermissions() {
  const [users, setUsers]         = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    ssUserAPI.list()
      .then(d => setUsers(d.data || d || []))
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, []);

  return (
    <Page>
      <PageHeader
        title="Permissions"
        subtitle="Inspect effective access for any user and run ad-hoc permission checks against the live decision engine."
      />
      {usersLoading ? <Spinner /> : (
        <>
          <EffectivePermsPanel users={users} />
          <PermissionChecker users={users} />
        </>
      )}
    </Page>
  );
}
